import { useQueryClient } from '@tanstack/react-query'

import {
  ComponentMetadataResponse,
  ComponentParameter
} from 'api/hooks/useGetComponentMetadata/types'
import {
  getComponentMetadataQueryKey,
  getCustomComponentMetadataQueryKey
} from 'api/hooks/useGetComponentMetadata/useGetComponentMetadata'
import { useCustomComponentSummary } from 'api/hooks/useGetComponentTree'
import { useGetComponentSummary } from 'api/hooks/useGetComponentTree/useGetComponentSummary'
import { useGetJobSummaries } from 'api/hooks/useGetJobSummaries'

import { useProjectInfo } from 'hooks/useProjectInfo/useProjectInfo'

import { EmeraldId } from 'job-lib/types/Job'
import { ParameterCollection } from 'job-lib/types/Parameters'

import { ValidationDependencies } from 'modules/ComponentValidation/types'
import { getValidationDependencies } from 'modules/ComponentValidation/utils/getValidationDependencies'

export const useValidationDependencies = () => {
  const { data } = useGetJobSummaries()
  const jobSummaries = data?.results ?? []
  const { getSummary } = useCustomComponentSummary()
  const { getByImplementationId } = useGetComponentSummary()
  const projectInfo = useProjectInfo()
  const queryClient = useQueryClient()

  const processParameter = (
    parameter: ComponentParameter,
    componentMetadata: ComponentMetadataResponse | undefined
  ): Record<string, string[]> => {
    let dependencyBag: Record<string, string[]> = {}

    if (parameter.validationDependencies) {
      dependencyBag = {
        ...dependencyBag,
        ...getValidationDependencies(
          parameter.validationDependencies,
          jobSummaries,
          componentMetadata
        )
      }
    }

    if (parameter.childProperties) {
      parameter.childProperties.forEach((childParameter) => {
        dependencyBag = {
          ...dependencyBag,
          ...processParameter(childParameter, componentMetadata)
        }
      })
    }

    return dependencyBag
  }
  const getDependencies = (
    implementationId: EmeraldId,
    parameters: ParameterCollection
  ) => {
    const componentId = getByImplementationId(
      implementationId.toString(),
      parameters
    )?.componentId as string
    const customSummary = getSummary(componentId)
    const componentMetadata: ComponentMetadataResponse | undefined =
      customSummary
        ? queryClient.getQueryData(
            getCustomComponentMetadataQueryKey(projectInfo, componentId)
          )
        : queryClient.getQueryData(
            getComponentMetadataQueryKey(projectInfo, componentId)
          )

    const validationDependencies: ValidationDependencies =
      componentMetadata?.metadata?.parameters.reduce(
        (
          dependencyBag: Record<string, string[]>,
          parameter: ComponentParameter
        ) => {
          return {
            ...dependencyBag,
            ...processParameter(parameter, componentMetadata)
          }
        },
        {}
      ) ?? {}

    return validationDependencies
  }

  return getDependencies
}
